import * as angular from "angular";
import { Filter } from "../models/filter.model";
import { PermissionCheck } from "../shared/permission";
import { LogsService } from "../services/logs.service";

export class LogsDataProvider {
    private $q: any;
    private $filter: any;
    private service: LogsService;
    private permissionProvider: PermissionCheck;

    public filters = new Filter();
    public logs: any[] = [];
    public infiniteItems: LogsInfiniteItems;

    constructor(
        $q: any,
        $filter: any,
        LogsService: LogsService,
        PermissionCheck: PermissionCheck
    ) {
        this.$q = $q;
        this.$filter = $filter;
        this.service = LogsService;
        this.permissionProvider = PermissionCheck;
        this.infiniteItems = new LogsInfiniteItems(this);
    }

    clearAll(): void {
        this.logs = [];
        this.infiniteItems.reset();
        this.filters = new Filter();
    }

    setFilters(filters: Filter): void {
        this.clearAll();
        this.filters = filters;
    }

    addToLogList(new_logs: object[]): void {
        if (new_logs.length < 50) {
            this.infiniteItems.end = true; //no more logs to load.
        }
        this.logs = this.logs.concat(new_logs);
        this.infiniteItems.numLoaded_ = this.logs.length;
        this.infiniteItems.loading = false;
    }

    getLogs(offset: number, limit: number, filters: object): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .all({ offset: offset, limit: limit, filters: filters })
            .then(
                (response: object) => {
                    const json = response["data"];
                    deferred.resolve(json.data);
                },
                (error: object) => deferred.reject(error)
            );
        return deferred.promise;
    }

    logToString(log?: any, uid?: string | number): string | null {
        if (log) {
            let last_updater_name =
                (log.ufname || "") + " " + (log.ulname || "").trim();
            let action = "";
            let ending = "";
            let subject = "";
            let noSubject = false;

            if (log.type == "invite") {
                if (log.extra1) {
                    subject =
                        "<span class='semi-bold'>" + log.extra1 + "</span>";
                } else {
                    subject =
                        "<span class='semi-bold'>+" +
                        log.extra2 +
                        "" +
                        log.extra3 +
                        "</span>";
                }

                switch (log.event) {
                    case "PERMISSION_CHANGED":
                        action = "updated the permission of the invitation for";
                        ending =
                            "to <span class='semi-bold'>" +
                            this.$filter("permissionName")(log.extra5) +
                            "</span>";
                        break;
                    case "REVOKED":
                        action = "deleted an invitation for";
                        break;
                    case "ACCEPTED":
                        action = "accepted the invitation for";
                        break;
                    case "REINVITED":
                        action = "resent the invitation for";
                        break;
                    case "CREATE":
                        action = "invited";
                        ending =
                            "with permission <span class='semi-bold'>" +
                            this.$filter("permissionName")(log.extra5) +
                            "</span>";
                        if (
                            this.permissionProvider.hasPermission("USERLIMIT")
                        ) {
                            if (log.extra4) {
                                ending += " and a user license";
                            } else {
                                ending += " without a user license";
                            }
                        }
                        break;
                    case "ACTIVATED":
                        action =
                            "granted a locked license to the invitation for";
                        break;
                    case "DEACTIVATED":
                        action =
                            "removed a locked license from the invitation for";
                        break;
                    case "RESEND":
                        action = "resent an invitation for";
                        break;
                }
            } else if (log.type == "bulkaction") {
                switch (log.event) {
                    case "HOUSE DELETE":
                        action =
                            "deleted <span class='semi-bold'>" +
                            log.count +
                            " houses</span>";
                        break;
                    case "STREET DELETE":
                        action =
                            "deleted <span class='semi-bold'>" +
                            log.count +
                            " streets</span>";
                        break;
                    case "CONTACT DELETE":
                        action =
                            "deleted <span class='semi-bold'>" +
                            log.count +
                            " contacts </span>";
                        break;
                    case "CONTACT ANONYMIZE":
                        action =
                            "anonymized <span class='semi-bold'>" +
                            log.count +
                            " contacts</span>";
                        break;
                    case "HOUSE GROUP":
                        action =
                            "added <span class='semi-bold'>" +
                            log.count +
                            " houses </span> to a list";
                        break;
                    case "CONTACT GROUP":
                        action =
                            "added <span class='semi-bold'>" +
                            log.count +
                            " contacts </span>  to a list";
                        break;
                    case "STREET GROUP":
                        action =
                            "added <span class='semi-bold'>" +
                            log.count +
                            " streets </span>  to a list";
                        break;
                }
                if (log.group && log.group.gid) {
                    action += ` - <a href='/#/people?group=%7B"gid":"${log.group.gid}","gname":"${log.group.gname}"%7D' class='md-primary'> ${log.group.gname} </a>`;
                }

                if (log.all_checked) {
                    action += " with the select all checked";
                }

                if (log.filters) {
                    action += ", using these ";
                }
            } else if (log.type == "subscription") {
                if (log.extra4) {
                    subject =
                        "<span class='semi-bold'>" + log.extra4 + "</span>";
                }
                switch (log.event) {
                    case "CREATE":
                        action = "created a subscription with plan";
                        break;
                    case "CANCEL":
                        action = " cancelled a subscription ";
                        subject = "";
                        break;
                    case "LICENSES_CHANGED":
                        action =
                            " updated the number of paid super user licenses on this subscription to <span class='semi-bold'>" +
                            log.extra2 +
                            "</span>";
                        subject = "";
                        break;
                    case "UPDATE":
                        action = "changed the plan of this subscription to";
                        break;
                }
            } else if (log.type == "campaignuser") {
                if (log.extra6) {
                    subject = log.extra6;

                    if (log.extra4 != 7) {
                        subject =
                            uid == log.extra1
                                ? " <span class='semi-bold'>" +
                                  subject +
                                  "</span> "
                                : " <a class='md-primary' href='#/user/" +
                                  log.extra1 +
                                  "'>" +
                                  subject +
                                  "</a> ";
                    }
                }
                switch (log.event) {
                    case "CREATE":
                        action = " added the user ";
                        break;
                    case "ACTIVATED":
                        action = " granted a locked license to ";
                        break;
                    case "ACTIVATED LOCKED":
                        action = " granted a locked license to ";
                        break;
                    case "ACTIVATED SHARED":
                        action = " was auto assigned a shared license ";
                        noSubject = true;
                        break;
                    case "DEACTIVATED":
                        action = " revoked a user license from ";
                        break;
                    case "DEACTIVATED LOCKED":
                        action = " revoked a locked license from ";
                        break;
                    case "DEACTIVATED SHARED":
                        action = " revoked a shared license from ";
                        break;
                    case "BILLING_ADDED":
                        action = " granted billing privileges to ";
                        break;
                    case "BILLING_REMOVED":
                        action = " revoked billing privileges from ";
                        break;
                    case "PERMISSION_CHANGED":
                        if (log.extra4 == 7) {
                            action = "removed ";
                            subject =
                                "<span class='semi-bold'>" +
                                log.extra6 +
                                "</span>";
                            ending = " from this account";
                        } else {
                            action = "changed the permission of ";
                            let permission =
                                this.permissionProvider.getPermission(
                                    log.extra4
                                );
                            if (permission) {
                                ending =
                                    "to <span class='semi-bold'>" +
                                    this.$filter("permissionName")(log.extra4) +
                                    "</span>";
                            }
                        }
                        break;
                }
            }

            if (action) {
                let link = "";
                if (log.ufname == "System") {
                    link = "A webhook ";
                } else {
                    link =
                        uid == log.uid
                            ? " <span class='semi-bold'>" +
                              last_updater_name +
                              "</span> "
                            : " <a class='md-primary' href='#/user/" +
                              log.uid +
                              "'>" +
                              last_updater_name +
                              "</a> ";
                }
                if (noSubject) {
                    return link + action + " " + ending.trim();
                } else {
                    return link + action + " " + subject + " " + ending.trim();
                }
            }
        }

        return null;
    }
}

export class LogsInfiniteItems {
    public numLoaded_ = 0;
    public toLoad_ = 0;
    public loading = false;
    public end = false;
    public limit: number | undefined = undefined;
    public dataProvider: LogsDataProvider;

    constructor(dataProvider: LogsDataProvider) {
        this.dataProvider = dataProvider;
    }

    getItemAtIndex(index: number): object | null {
        if (index > this.numLoaded_) {
            this.fetchMoreItems_(index);
            return null;
        }
        return this.dataProvider.logs[index];
    }

    getLength(): number {
        return this.end ? this.numLoaded_ : this.numLoaded_ + 50;
    }

    fetchMoreItems_(index: number): void {
        if (this.toLoad_ < index) {
            this.loading = true;
            this.toLoad_ += 50;
            this.limit = 50;

            let offset: any = null;
            if (this.dataProvider.logs[this.dataProvider.logs.length - 1]) {
                offset = {
                    id: this.dataProvider.logs[
                        this.dataProvider.logs.length - 1
                    ].id,
                    timestamp:
                        this.dataProvider.logs[
                            this.dataProvider.logs.length - 1
                        ].timestamp,
                    type: this.dataProvider.logs[
                        this.dataProvider.logs.length - 1
                    ].type,
                };
            }

            this.dataProvider
                .getLogs(offset, this.limit, this.dataProvider.filters)
                .then((new_logs: object[]) => {
                    this.dataProvider.addToLogList(new_logs);
                });
        }
    }

    reset(): void {
        this.numLoaded_ = 0;
        this.toLoad_ = 0;
        this.end = false;
    }
}
angular.module("vcDataProvider").service("LogsDataProvider", LogsDataProvider);
